import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Search, 
  MapPin, 
  TrendingUp, 
  Clock, 
  Building2, 
  Loader2,
  Filter,
  ChevronRight,
  AlertCircle,
  Lock
} from 'lucide-react';

const AppOpportunitiesPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useI18n();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    project_type: '',
    status: ''
  });

  const isVerified = user?.verification_status === 'approved';

  useEffect(() => {
    fetchOpportunities();
  }, [filters]);

  const fetchOpportunities = async () => {
    try {
      const params = {};
      if (filters.project_type) params.project_type = filters.project_type;
      if (filters.status) params.status = filters.status;
      
      const response = await api.get('/opportunities', { params });
      let data = response.data.opportunities || response.data || [];
      
      // Client-side search filter
      if (filters.search) {
        const search = filters.search.toLowerCase();
        data = data.filter(opp => 
          opp.title?.toLowerCase().includes(search) ||
          opp.location_city?.toLowerCase().includes(search)
        );
      }
      
      setOpportunities(data);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Abierta' },
      soft_circle: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Soft Circle' },
      fully_funded: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Fondeada' },
      closed: { className: 'bg-muted text-muted-foreground border-muted', label: 'Cerrada' },
    };
    const style = styles[status] || styles.open;
    return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
  };

  return (
    <div className="space-y-8" data-testid="app-opportunities-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="heading-2 mb-2">Oportunidades de Inversión</h1>
          <p className="body-text">
            Explore las oportunidades de inversión disponibles.
          </p>
        </div>
      </div>

      {/* Verification Alert */}
      {!isVerified && (
        <Alert className="border-amber-500/20 bg-amber-500/5">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-amber-500">
            <strong>Cuenta no verificada:</strong> Puede ver las oportunidades pero necesita completar su verificación para invertir.{' '}
            <Link to="/app/profile" className="underline">Completar perfil</Link>
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card className="card-dashboard">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o ubicación..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
                data-testid="search-opportunities"
              />
            </div>
            <Select
              value={filters.project_type}
              onValueChange={(v) => setFilters(prev => ({ ...prev, project_type: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Tipo de proyecto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="residential">Residencial</SelectItem>
                <SelectItem value="commercial">Comercial</SelectItem>
                <SelectItem value="mixed_use">Uso Mixto</SelectItem>
                <SelectItem value="industrial">Industrial</SelectItem>
                <SelectItem value="land">Terreno</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="open">Abiertas</SelectItem>
                <SelectItem value="soft_circle">Soft Circle</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Opportunities Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
        </div>
      ) : opportunities.length === 0 ? (
        <div className="text-center py-16">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
          <p className="text-muted-foreground">No se encontraron oportunidades</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {opportunities.map((opp) => (
            <Link 
              key={opp.opportunity_id} 
              to={`/app/opportunities/${opp.slug}`}
              className="group"
            >
              <Card className="card-dashboard h-full hover:border-primary/30 transition-colors">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex justify-between items-start mb-4">
                    {getStatusBadge(opp.status)}
                    {opp.is_featured && (
                      <Badge variant="outline" className="border-primary/30 text-primary">Destacada</Badge>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-medium mb-2 group-hover:text-primary transition-colors">
                    {opp.title}
                  </h3>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <MapPin className="h-4 w-4" strokeWidth={1.5} />
                    <span>{opp.location_city}, {opp.location_state}</span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-6 flex-grow">
                    {opp.summary}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">TIR Objetivo</p>
                      <p className="font-medium text-primary">
                        {opp.projected_irr_base}-{opp.projected_irr_upside}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Inversión Mín.</p>
                      <p className="font-medium">{formatCurrency(opp.minimum_investment)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Plazo</p>
                      <p className="font-medium">{opp.hold_period_months || '--'} meses</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Progreso</p>
                      <p className="font-medium">{Math.round((opp.funding_progress || 0) * 100)}%</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Ver detalles</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.5} />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Risk Disclaimer */}
      <Card className="border-muted bg-muted/30">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground">
            <strong>Nuestro Compromiso:</strong> Como fondo institucional de alto nivel, implementamos las mejores prácticas y estructuras legales para proteger su inversión. Estamos aquí para construir una relación de largo plazo basada en confianza y resultados.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppOpportunitiesPage;
